package com.corbettcode.allaboutrocks.pages.typesof

import androidx.compose.runtime.Composable
import com.corbettcode.allaboutrocks.BOOK_COVER_MAX_WIDTH
import com.corbettcode.allaboutrocks.FIGURE_IMAGE_MAX_WIDTH
import com.corbettcode.allaboutrocks.HERO_IMAGE_MAX_WIDTH
import com.corbettcode.allaboutrocks.HeadlineTextStyle
import com.corbettcode.allaboutrocks.HeroContainerStyle
import com.corbettcode.allaboutrocks.ImageCreditStyle
import com.corbettcode.allaboutrocks.PageHeadlineStyle
import com.corbettcode.allaboutrocks.PageListStyle
import com.corbettcode.allaboutrocks.PageSectionTitleStyle
import com.corbettcode.allaboutrocks.SectionTextStyle
import com.corbettcode.allaboutrocks.components.layouts.PageLayoutData
import com.corbettcode.allaboutrocks.components.widgets.CarouselOfSections
import com.corbettcode.allaboutrocks.components.widgets.Figure
import com.corbettcode.allaboutrocks.pages.LearnBookRatingStyle
import com.corbettcode.allaboutrocks.pages.LearnBookTitleStyle
import com.corbettcode.allaboutrocks.utility.textColor
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Alignment.CenterHorizontally
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.data.add
import com.varabyte.kobweb.core.init.InitRoute
import com.varabyte.kobweb.core.init.InitRouteContext
import com.varabyte.kobweb.core.layout.Layout
import com.varabyte.kobweb.silk.components.graphics.Image
import com.varabyte.kobweb.silk.components.layout.HorizontalDivider
import com.varabyte.kobweb.silk.components.layout.SimpleGrid
import com.varabyte.kobweb.silk.components.layout.numColumns
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.toAttrs
import com.varabyte.kobweb.silk.style.toModifier
import org.jetbrains.compose.web.css.cssRem
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.B
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Em
import org.jetbrains.compose.web.dom.Li
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.Text
import org.jetbrains.compose.web.dom.Ul
import kotlin.time.Duration.Companion.seconds

// --- Constants ---
//private val HERO_IMAGE_MAX_WIDTH = 600.px
//private val FIGURE_IMAGE_MAX_WIDTH = 600.px
//private val BOOK_COVER_MAX_WIDTH = 400.px

@InitRoute
fun initTypesOfPetrifiedWood(ctx: InitRouteContext) {
    ctx.data.add(PageLayoutData("Types of Rocks"))
}

/** The steps a log has to survive to come out the other side as stone. */
private val petrifiedWoodFormation = listOf(
    "Rapid burial" to "A fallen tree has to be covered fast — by volcanic ash, river sand, or lake mud — so that it is sealed away from oxygen and from the fungi and insects that would otherwise reduce it to nothing."
    , "Silica-rich groundwater" to "Water moving through volcanic ash picks up dissolved silica and carries it into the buried log. Volcanic terrain is where most petrified wood forms, because that is where the silica is."
    , "Permineralization" to "Silica precipitates inside the empty cell cavities and within the cell walls themselves, casting the wood's microscopic plumbing in quartz while the original structure is still standing."
    , "Replacement" to "Organic material breaks down and silica takes its place. When the swap happens slowly and in order, cell walls, growth rings, and even insect damage survive in stone."
    , "Uplift and erosion" to "Tens of millions of years later the covering sediment is stripped away and the logs weather out onto the surface, which is where collectors meet them."
)

/** What to check when you think you have a piece. */
private val petrifiedWoodKeyCharacteristics = listOf(
    "Grain that runs one way" to "This is the single best test. Fibers, rays, and knots line up along the length of the piece just as they did in the living tree, and a cross-section shows rings curving around a center."
    , "Hardness" to "6.5–7 on the Mohs scale where the wood is fully silicified, so it will scratch glass and take a mirror polish. Incompletely replaced \"punky\" wood is softer and crumbles at the edges."
    , "Weight" to "Noticeably heavy for its size. It is solid quartz now, not wood, and it feels like it."
    , "Fracture" to "Conchoidal, like any silica rock, but breaks tend to follow the old grain — which is why weathered logs snap crosswise into neat rounds instead of splintering lengthwise."
    , "Bark, limbs, and borings" to "Outer surfaces often keep bark texture, branch scars, and the tunnels of insects that were at work in the wood before it was buried."
    , "Color from trace elements" to "Iron gives red, orange, and yellow; manganese gives pink and purple; carbon gives black; traces of copper and chromium give green and blue."
)

/** Named petrified wood, by where it comes from and what makes it recognizable. */
private val petrifiedWoodVarieties = listOf(
    "Rainbow wood (Arizona)" to "Triassic logs from the Chinle Formation, largely the conifer Araucarioxylon arizonicum, banded in red, yellow, and purple by iron and manganese. The classic material, and the reason Petrified Forest National Park exists."
    , "Ginkgo and Vantage wood (Washington)" to "Miocene logs entombed by Columbia River basalt flows near Vantage. More than fifty species have been identified there, including the ginkgo the state park is named for."
    , "Hampton Butte wood (Oregon)" to "Green jasper-replaced wood from central Oregon, among the most sought-after material in the Pacific Northwest and increasingly hard to come by."
    , "Opalized wood (Nevada)" to "Virgin Valley wood replaced by precious opal, with fire running along the grain. It is famously unstable — most of it is stored in water, because it crazes as it dries out."
    , "Blue Forest wood (Wyoming)" to "Eden Valley limb casts sheathed in blue-grey chalcedony that formed around the wood in an algal crust, leaving a stone that is agate outside and wood inside."
    , "Palm wood (Texas and Louisiana)" to "Petrified palm, which has rod-like dots in cross-section rather than growth rings, because palms are not true woody trees. It is the state stone of Texas and the state fossil of Louisiana."
    , "Peanut wood (Australia)" to "Dark wood peppered with white oval patches — clam borings in driftwood that later filled with pale sediment and silica."
    , "Limb casts" to "Silica that filled the hollow left by a branch that burned or rotted away inside volcanic ash. The outside keeps the branch's shape while the inside is solid agate with no cell structure at all."
)
//
//@Composable
//private fun PetrifiedWoodFigure(
//    source: String
//    , alt: String
//    , caption: String
//    , credit: String
//) {
//    Column(
//        Modifier
//            .fillMaxWidth()
//            .gap(0.5.cssRem)
//        , horizontalAlignment = CenterHorizontally
//    ) {
//        Image(
//            src = source
//            , alt = alt
//            , modifier = Modifier
//                .maxWidth(FIGURE_IMAGE_MAX_WIDTH)
//                .fillMaxWidth()  // shrinks naturally on small screens
//        )
//        P { Em { Text(caption) } }
//        SpanText(credit, ImageCreditStyle.toModifier())
//    }
//}

@Composable
private fun PetrifiedWoodSectionTitle(text: String) {
    SpanText(
        text
        , PageSectionTitleStyle.toModifier()
            .color(textColor())
    )
}

@Composable
private fun TypesOfPetrifiedWoodHeadline() {
    Div(HeadlineTextStyle.toAttrs()) {
        Column {
            SpanText(
                "Petrified Wood"
                , PageHeadlineStyle.toModifier()
                    .color(textColor())
                    .align(alignment = CenterHorizontally)
            )
        }
    }
}

@Composable
private fun TypesOfPetrifiedWoodHero() {

    TypesOfPetrifiedWoodHeadline()

    // 1 column on mobile, 2 columns on md+ (image left, text right)
    SimpleGrid(
        numColumns = numColumns(1, md = 2)
        , modifier = HeroContainerStyle
            .toModifier()
            .fillMaxWidth()
            .gap(2.cssRem)
    ) {
        Box {
            Column(Modifier.gap(2.cssRem)) {
                Image(
                    src = "/images/typesof/petrifiedwood/petrified_logs_hillside.jpg"
                    , alt = "Petrified logs weathering out of a desert hillside"
                    , modifier = Modifier
                        .maxWidth(HERO_IMAGE_MAX_WIDTH)
                        .fillMaxWidth()  // shrinks naturally on small screens
                )
                P { Em { Text("Petrified logs weathering out of a hillside in Petrified Forest National Park, Arizona — the cut end of the near log is solid chalcedony.") } }
                SpanText(
                    "Photo: Eric Kilby, CC BY-SA 2.0, via Wikimedia Commons"
                    , ImageCreditStyle.toModifier()
                )
            }
        }

        Column(Modifier.gap(2.cssRem)) {
            P {
                Text("Petrified wood is a fossil, not a rock type of its own. It starts as an ordinary tree, gets buried before it can rot, and is slowly rebuilt in silica until every cell is filled and replaced with quartz. What is left looks like a log and often still shows bark, knots, and growth rings, but it is stone all the way through — and heavy enough that picking a piece up is usually the moment it clicks.")
            }

            PetrifiedWoodSectionTitle("How It Forms")
            P {
                Text("Petrification is a race between decay and mineralization, and the wood only wins if it is buried fast and the groundwater is carrying enough dissolved silica. That is why the great petrified wood deposits sit in volcanic country: ash supplies the silica, and ash falls and lahars supply the quick burial.")
            }
        }
    }

    Column(
        HeroContainerStyle
            .toModifier()
            .fillMaxWidth()
            .gap(2.cssRem)
    ) {
        Ul(PageListStyle.toAttrs()) {
            petrifiedWoodFormation.forEach { (name, description) ->
                Li {
                    B { Text(name) }
                    Text(" — $description")
                }
            }
        }

        Figure(
            source = "/images/typesof/petrifiedwood/log_cross_section.jpg"
            , alt = "Cross-section of a petrified log showing rings and colored silica"
            , caption = "The cut end of a petrified log — growth rings, radial cracks, and heartwood are all still readable, now rendered in colored silica."
            , credit = "Photo: Daniel Schwen, CC BY-SA 3.0, via Wikimedia Commons"
        )
    }

    HorizontalDivider(Modifier
        .fillMaxWidth()
        .margin(topBottom = 2.cssRem)
    )

    Column(
        Modifier
            .fillMaxWidth()
            .gap(2.cssRem)
    ) {
        PetrifiedWoodSectionTitle("Key Characteristics")

        Ul(PageListStyle.toAttrs()) {
            petrifiedWoodKeyCharacteristics.forEach { (name, description) ->
                Li {
                    B { Text(name) }
                    Text(": $description")
                }
            }
        }

        Figure(
            source = "/images/typesof/petrifiedwood/petrified_logs_arizona.jpg"
            , alt = "Two red petrified log segments with visible lengthwise grain"
            , caption = "Log segments in Arizona — the grain runs the length of each piece and the breaks cut cleanly across it, the pattern that separates petrified wood from a patterned jasper."
            , credit = "Photo: National Park Service, public domain"
        )

        Figure(
            source = "/images/typesof/petrifiedwood/silicified_log_winter.jpg"
            , alt = "Petrified log with white and red silica exposed on a broken face"
            , caption = "A broken face on a petrified log — white and grey chalcedony inside, iron-stained red at the rind."
            , credit = "Photo: Kumar Appaiah, CC BY-SA 2.0, via Wikimedia Commons"
        )

        PetrifiedWoodSectionTitle("Types and Varieties")
        P {
            Text("Petrified wood is usually named for where it came from, since each deposit has its own species, mineralogy, and colors:")
        }

        Ul(PageListStyle.toAttrs()) {
            petrifiedWoodVarieties.forEach { (name, description) ->
                Li {
                    B { Text(name) }
                    Text(" — $description")
                }
            }
        }

        Figure(
            source = "/images/typesof/petrifiedwood/ginkgo_petrified_forest_wa.jpg"
            , alt = "Petrified log at Ginkgo Petrified Forest State Park, Washington"
            , caption = "A log at Ginkgo Petrified Forest State Park near Vantage, Washington — Miocene trees drowned and then buried by Columbia River basalt flows."
            , credit = "Photo: Thayne Tuason, CC BY-SA 4.0, via Wikimedia Commons"
        )

        PetrifiedWoodSectionTitle("Petrified Wood and Jasper")
        P {
            Text("These two overlap, which causes a lot of confusion. Jasper is opaque microcrystalline quartz colored by iron and other impurities; petrified wood is a fossil defined by what it used to be. When the silica that replaced a log happens to be jasper, the piece is correctly called jasperized wood — a fossil and a jasper at the same time. The way to tell a patterned jasper from petrified wood is structure rather than color: jasper's bands and swirls wander, while wood grain runs in one direction and rings curve consistently around a center.")
        }

        PetrifiedWoodSectionTitle("Where to Find It")
        P {
            Text("Petrified wood shows up wherever a forest was buried in volcanic ash or river sediment and later re-exposed. Arizona, Washington, Oregon, Nevada, Wyoming, Utah, and Texas all produce it in quantity, and it is common enough in the Pacific Northwest that stream gravels and roadcuts are worth a look. Because it is harder than the mudstone and ash around it, it survives as loose float on badland slopes and in creek beds long after the enclosing rock has washed away.")
        }

        Figure(
            source = "/images/typesof/petrifiedwood/petrified_wood_badlands.jpg"
            , alt = "Broken petrified log segments scattered across a badlands slope"
            , caption = "A log breaking into rounds on a badlands slope — soft sediment erodes away and the silicified wood is left lying on the surface."
            , credit = "Photo: Jon Zander, CC BY-SA 3.0, via Wikimedia Commons"
        )

        PetrifiedWoodSectionTitle("Collecting Rules")
        P {
            Text("Petrified wood is one of the few materials with its own federal collecting rule, so it is worth knowing before you fill a bucket:")
        }

        Ul(PageListStyle.toAttrs()) {
            Li {
                B { Text("National parks: none at all") }
                Text(" — Collecting in Petrified Forest National Park is illegal, down to the smallest chip, and the same goes for state parks such as Ginkgo. Look, photograph, leave it.")
            }
            Li {
                B { Text("BLM land: 25 pounds per day") }
                Text(" — Federal rules allow free use of petrified wood on most BLM-managed public land: up to 25 pounds plus one piece per person per day, and no more than 250 pounds per person per year.")
            }
            Li {
                B { Text("Personal use only") }
                Text(" — Collected wood may not be sold or bartered without a permit, quotas may not be pooled to take a single large piece, and anything over 250 pounds needs a permit of its own.")
            }
            Li {
                B { Text("Check the land status first") }
                Text(" — Private ground needs permission, and national forests, state land, and tribal land each have their own rules. The agency office covering the area is the place to ask.")
            }
        }

        PetrifiedWoodSectionTitle("Working With It")
        P {
            Text("Fully silicified wood is a rewarding lapidary material. It slabs cleanly, takes a high polish, and is hard enough to wear well, which is why it turns up as cabochons, bookends, clock faces, spheres, and tabletops. Cut across the grain for rings and a bullseye; cut along it for long streaks of color. Punky or weather-checked material is the one thing to avoid — it will come apart on the saw — and opalized wood needs slow, careful drying before it is worth cutting at all.")
        }
    }
}

@Composable
private fun TypesOfPetrifiedWood_BooksSection() {
    Div(SectionTextStyle.toAttrs()) {
        Column {
            SpanText(
                "Books on Petrified Wood"
                , PageSectionTitleStyle.toModifier()
                    .color(textColor())
                    .align(alignment = CenterHorizontally)
                    .margin(topBottom = 2.cssRem)
            )
        }

        CarouselOfSections(
            items = booksOnPetrifiedWoodCarouselSlides
            , frameDuration = 8.seconds
        )
    }
}

@Page("/types_of_rocks/PetrifiedWood")
@Layout(".components.layouts.PageLayout")
@Composable
fun TypesOfPetrifiedWoodPage() {
    TypesOfPetrifiedWoodHero()
    HorizontalDivider(Modifier
        .fillMaxWidth()
        .margin(topBottom = 2.cssRem)
    )
    TypesOfPetrifiedWood_BooksSection()
}

/**
 * Field guides that cover petrified wood alongside the rest of what you will find in the same ground.
 */
val booksOnPetrifiedWoodCarouselSlides = listOf<@Composable () -> Unit>(
    {
        SimpleGrid(
            numColumns = numColumns(2)
            , modifier = Modifier.fillMaxWidth()
        ) {
            Image(
                src = "/images/learn/books/washington_oregon_rocks_and_minerals.jpg"
                , alt = "Washington and Oregon Rocks and Minerals field guide"
                , modifier = Modifier
                    .maxWidth(BOOK_COVER_MAX_WIDTH)
                    .fillMaxWidth()  // shrinks naturally on small screens
            )

            Column {
                SpanText(
                    "Washington & Oregon Rocks & Minerals: A Field Guide to the Evergreen and Beaver States"
                    , LearnBookTitleStyle.toModifier()
                        .color(textColor())
                        .align(alignment = Alignment.Start)
                )
                SpanText(
                    "Dan R. Lynch & Bob Lynch"
                    , LearnBookRatingStyle.toModifier()
                        .color(textColor())
                        .align(alignment = Alignment.Start)
                )

                P {
                    Text("The most useful book to have in the truck if you are hunting petrified wood in the Pacific Northwest. Both states are full of it — the Columbia River basalts buried whole forests — and this guide covers it alongside the agate, jasper, and zeolites you will turn up in the same gravel bars. Entries are photographed rather than drawn, and organized by what a specimen looks like in the hand, so you can work from what you are holding instead of from a name you do not have yet.")
                }
            }
        }
    }
)
